import { DerivedAvailabilityStatusInternal } from '@commerce-product-webcomponents/sdk';
import {
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonWidth,
  Theme,
  useTranslation,
  useSession,
} from '@nextgen-web-framework/all';
import { formatPrice } from '@nextgen-web-framework/core';
import { useState } from 'react';
import { ProductLink } from '../Hoc';
import {
  BasicDetailsComponent,
  DerivedAvailabilityStatusComponentGroupKey,
  DetailsType,
  IdAndSizeType,
  ImageComponent,
  ImageType,
  includesDerivedAvailabilityStockStatus,
  ProductIdAndSizeComponent,
  StockStatusComponent,
  StockStatusComponentType,
} from '../Molecules';
import APointRedeemInfoDrawer from '../Molecules/APointRedeemInfoDrawer/APointRedeemInfoDrawer';
import styles from './APointRedeemCard.module.scss';
import { type APointRedeemCardPropsTypes } from './types/propsTypes';

const APointRedeemCard = (props: APointRedeemCardPropsTypes): JSX.Element => {
  const {
    productData,
    currency,
    showAPointAdditionalInfo,
    productQuantity,
    isFlexibleWidthCard = false,
    dateSubText,
  } = props;

  const {
    brandName,
    productName,
    mainLink,
    alt,
    productId,
    productSize,
    aPointPrice,
    aPointPriceCompanionText,
    variantGroupId,
    productFlags,
    derivedAvailabilityStatus,
    firstAvailableFutureDate,
  } = productData;

  const [showApointRedeemDrawer, setShowApointRedeemDrawer] = useState(false);

  const t = useTranslation();
  const { isABO } = useSession();

  const aPointPayment = aPointPriceCompanionText?.payment;
  const aPointDiscount = aPointPriceCompanionText?.discount;
  const conditionalAPointPrice = aPointPrice ?? '';
  const aPointText = `${conditionalAPointPrice} ${t('product.APointRedeemCard.aPointText')}`;
  const restrictFromPdpDisplay = productFlags?.restrictFromPdpDisplay;
  const productLinkProps = {
    className: styles['width-inherit'],
    showLink: restrictFromPdpDisplay ? false : true,
    href: '',
    productId,
    productName,
    variantGroupId,
  };

  const getAPointCompanionText = () => {
    let aPointCompanionText = '';
    if (aPointPayment !== undefined) {
      aPointCompanionText = t('product.APointRedeemCard.aPointCompanionPaymentText');
      aPointCompanionText = aPointCompanionText.replace('{payment}', formatPrice(aPointPayment, currency));
    }
    if (aPointDiscount !== undefined) {
      aPointCompanionText = t('product.APointRedeemCard.aPointCompanionDiscountText');
      aPointCompanionText = aPointCompanionText.replace('{points}', conditionalAPointPrice.toString());
      aPointCompanionText = aPointCompanionText.replace('{discount}', aPointDiscount.toString());
    }
    return aPointCompanionText;
  };

  const image = (type: string) => (
    <ProductLink {...productLinkProps}>
      <div className={`${styles['product-image-container']} ${styles['width-inherit']}`}>
        <ImageComponent link={mainLink} alt={alt} imagetype={type} />
      </div>
    </ProductLink>
  );

  const basicDetails = (type: string, mainText = brandName, subText = productName) => (
    <ProductLink {...productLinkProps}>
      <div className={styles['cpw-basic-details']}>
        <BasicDetailsComponent brandName={mainText} productName={subText} detailsType={type} />
      </div>
    </ProductLink>
  );

  const handleRedeemBtnClick = () => {
    setShowApointRedeemDrawer(true);
  };

  const handleApointRedeemInfoDrawerCloseBtnClick = () => {
    setShowApointRedeemDrawer(false);
  };

  return (
    <>
      {!showApointRedeemDrawer && (
        <div
          className={`${styles['cpw-apoint-redeem-card']}  ${isFlexibleWidthCard && styles['flexible-card-width']}`}
          data-testid="cpw-apoint-redeem-card-test-id"
        >
          <div className={`${styles['cpw-bundle-container']}`} data-testid="apoint-redeem-card-container-testID">
            <div className={styles['product-image']} data-testid="apoint-redeem-card-image-testID">
              {image(ImageType.APOINT_REDEEM_CARD)}
            </div>
            <div className={styles['product-description']}>
              <div>
                {basicDetails(DetailsType.APOINT_REDEEM_PRODUCT_CARD)}
                <ProductIdAndSizeComponent
                  productId={productId}
                  IdSizeType={IdAndSizeType.ID_SIZE_APOINT_CARD}
                  showProductId={showAPointAdditionalInfo?.showProductId || false}
                  productSize={showAPointAdditionalInfo?.showProductSize ? productSize : ''}
                />
              </div>
              <div className={styles['product-points-info']}>
                {basicDetails(DetailsType.APOINT_REDEEM_CARD_PRICE_DETAILS, aPointText, getAPointCompanionText())}
              </div>
              {derivedAvailabilityStatus && (
                <StockStatusComponent
                  derivedAvailabilityStatus={derivedAvailabilityStatus}
                  dateSubText={dateSubText}
                  firstAvailableFutureDate={firstAvailableFutureDate}
                  showSubText={true}
                  stockStatusType={StockStatusComponentType.STOCK_STATUS_SEARCH_CARD}
                />
              )}
              {!isABO() && (
                <div className={styles['apoint-product-redeem-btn']}>
                  <Button
                    id={'redeem-apoint'}
                    title={'Redeem'}
                    buttonStyle={ButtonStyles.SOLID}
                    theme={Theme.BLACK}
                    size={ButtonSize.SMALL}
                    tabIndex={0}
                    disabled={includesDerivedAvailabilityStockStatus(
                      derivedAvailabilityStatus as DerivedAvailabilityStatusInternal,
                      DerivedAvailabilityStatusComponentGroupKey.CTA_DISABLED_STATUSES,
                    )}
                    data-testid="redeem-apoint-btn"
                    onClick={handleRedeemBtnClick}
                    buttonWidth={ButtonWidth.FULL_WIDTH}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {showApointRedeemDrawer && (
        <APointRedeemInfoDrawer
          productData={productData}
          productQuantity={productQuantity}
          onClose={handleApointRedeemInfoDrawerCloseBtnClick}
        />
      )}
    </>
  );
};

export default APointRedeemCard;
export type { APointRedeemCardPropsTypes };
